import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import { parseApiError } from '@/lib/errors';

import { useAuth } from './AuthContext';
import { LoginRequest, type LoginRequest as LoginRequestType } from './schemas';

/**
 * Migrated to TS + react-hook-form + zod.
 * Keeps the same visual design as the legacy LoginPage.
 */
export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname ?? ROUTES.PROJECTS;

  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<LoginRequestType>({
    resolver: zodResolver(LoginRequest),
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
  });

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  // If already authenticated, bounce to the intended destination
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (err) {
      setSubmitError(parseApiError(err) || 'Login failed');
    }
  });

  return (
    <div className="flex min-h-screen">
      {/* Branding panel */}
      <div className="relative hidden flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-800 md:flex md:w-[60%]">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 49px, rgba(255,255,255,0.3) 49px, rgba(255,255,255,0.3) 50px), repeating-linear-gradient(90deg, transparent, transparent 49px, rgba(255,255,255,0.3) 49px, rgba(255,255,255,0.3) 50px)',
          }}
          aria-hidden="true"
        />
        <div
          className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-indigo-600/20 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-blue-600/15 blur-3xl"
          aria-hidden="true"
        />

        <div className="relative z-10 px-12 text-center">
          <img src="/cpcon-logo-white.png" alt="CPCON" className="mx-auto mb-6 w-52" />
          <h1 className="mb-2 text-3xl font-bold tracking-tight text-white">CMMS Survey System</h1>
          <p className="text-base text-white/50">Fixed Asset Verification Platform</p>
        </div>

        <p className="absolute bottom-6 text-xs tracking-wider text-white/20">
          &copy; 2026 CPCON Group
        </p>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center bg-white px-6 py-12 md:w-[40%]">
        <div
          className={`w-full max-w-sm transition-all duration-700 ease-out ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <div className="mb-8 text-center md:hidden">
            <img src="/cpcon-logo.png" alt="CPCON" className="mx-auto mb-3 w-32" />
            <p className="text-sm text-gray-400">CMMS Survey System</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">Welcome back</h2>
            <p className="mt-1 text-sm text-gray-400">Sign in to your dashboard</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            {submitError && (
              <div
                role="alert"
                className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600"
              >
                <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {submitError}
              </div>
            )}

            <div>
              <label
                htmlFor="login-email"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Email
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="admin@cpcongroup.com"
                aria-invalid={Boolean(form.formState.errors.email)}
                aria-describedby={form.formState.errors.email ? 'login-email-error' : undefined}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder-gray-300 transition-all focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                {...form.register('email')}
              />
              {form.formState.errors.email && (
                <p id="login-email-error" className="mt-1 text-xs text-red-600">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  aria-invalid={Boolean(form.formState.errors.password)}
                  aria-describedby={
                    form.formState.errors.password ? 'login-password-error' : undefined
                  }
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pr-11 text-sm text-gray-800 placeholder-gray-300 transition-all focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  {...form.register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
              {form.formState.errors.password && (
                <p id="login-password-error" className="mt-1 text-xs text-red-600">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              loading={form.formState.isSubmitting}
              className="w-full rounded-xl shadow-lg shadow-indigo-600/20 hover:shadow-indigo-600/30"
            >
              {form.formState.isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-8 text-center text-[10px] uppercase tracking-wider text-gray-300">
            PM Dashboard &middot; CPCON Group
          </p>
        </div>
      </div>
    </div>
  );
}
